import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Flag, AlertTriangle, Check } from 'lucide-react';
import { api } from '../api/client';
import { useTranslation } from '../i18n';
import './ReportModal.css';

export default function ReportModal({ contentType, contentId, onClose }) {
  const { t } = useTranslation();
  const [reason,  setReason]  = useState('');
  const [details, setDetails] = useState('');
  const [step,    setStep]    = useState('form');
  const [saving,  setSaving]  = useState(false);
  const [errMsg,  setErrMsg]  = useState('');

  const reasons = t('report.reasons') || [];
  const typeKey = contentType === 'prayer_wall' ? 'prayer_wall' : (contentType || 'content');
  const label   = t('report.' + typeKey) || contentType || 'content';

  async function handleSubmit(e) {
    e.preventDefault();
    if (!reason) return;
    setSaving(true);
    try {
      await api.post('/api/reports', { contentType, contentId, reason, details });
      setStep('success');
    } catch (err) {
      setErrMsg(err.message || t('report.couldNotSubmit'));
      setStep('error');
    } finally { setSaving(false); }
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal report-modal">
        <div className="modal-header">
          <span className="modal-title">
            <Flag size={16} style={{marginRight: 6}}/>
            {t('report.title', label)}
          </span>
          <button className="modal-close" onClick={onClose}><X size={16}/></button>
        </div>

        {step === 'success' && (
          <div className="report-success">
            <div className="report-success-icon"><Check size={28}/></div>
            <h3>{t('report.successTitle')}</h3>
            <p>{t('report.successBody', label)}</p>
            <button className="btn btn-primary" onClick={onClose}>{t('report.done')}</button>
          </div>
        )}

        {step === 'error' && (
          <div className="report-error">
            <AlertTriangle size={24}/>
            <p>{errMsg}</p>
            <button className="btn btn-outline" onClick={() => setStep('form')}>
              {t('report.errorRetry')}
            </button>
          </div>
        )}

        {step === 'form' && (
          <form onSubmit={handleSubmit} className="report-form">
            <p className="report-intro">{t('report.intro', label)}</p>
            <div className="report-reasons">
              {reasons.map(r => (
                <button
                  key={r.value}
                  type="button"
                  className={'report-reason-btn ' + (reason === r.value ? 'active' : '')}
                  onClick={() => setReason(r.value)}
                >
                  {r.label}
                  {reason === r.value && <Check size={14} className="report-check"/>}
                </button>
              ))}
            </div>
            {reason && (
              <div className="report-details">
                <label className="set-form-label">
                  {t('report.detailsLabel')}{' '}
                  <span style={{fontWeight: 400, color: 'var(--gray-3)'}}>
                    {'(' + t('report.detailsOptional') + ')'}
                  </span>
                </label>
                <textarea
                  className="set-form-input"
                  rows={3}
                  placeholder={t('report.detailsPlaceholder')}
                  value={details}
                  onChange={e => setDetails(e.target.value)}
                  maxLength={500}
                  style={{resize: 'none'}}
                />
              </div>
            )}
            <div className="report-footer">
              <p className="report-note">{t('report.note')}</p>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!reason || saving}
                style={{gap: 6}}
              >
                {saving
                  ? <span className="spinner" style={{width: 14, height: 14}}/>
                  : <Flag size={14}/>}
                {saving ? t('report.submitting') : t('report.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
